import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import moment from 'moment';
import { each, indexOf, isArray, reduce, flatten, uniq } from 'lodash';
import MoneyDisplay from '../money-display';
import NumberDisplay from '../number-display';
import DateTimeDisplay from '../date-time-display';

class CustomMetaFieldRenderer extends Component {
  renderBooleanValue(value, key) {
    const { isCustomMeta } = this.props;
    if (isCustomMeta) {
      return (
        <Fa
          key={key}
          style={{ cursor: 'pointer', fontSize: '1.3em' }}
          name={value ? 'check-square-o' : 'square-o'}
          className={value ? 'text-success' : 'text-muted'}
        />
      );
    }
    return (<span key={key} className={value ? 'label label-success' : 'label label-danger'}>{(value ? 'YES' : 'NO')}</span>);
  }

  renderNumberValue(value, precision, key) {
    return (<NumberDisplay key={key} value={value} precision={precision} />);
  }

  renderCurrencyValue(value, key) {
    return (<MoneyDisplay key={key} value={value} precision={2} abbreviated />);
  }

  renderDateValue() {
    const { value } = this.props;
    return (<DateTimeDisplay value={value} format="short" />);
  }

  renderPicklistValue(finalValue, key) {
    if (finalValue === null) {
      return null;
    }
    return <span key={key}>{finalValue}</span>;
  }
  
  renderIdValue(value, key) {
    return <code key={key}>{value}</code>;
  }


  renderArray(values) {
    const { column: { type, name } } = this.props;
    const comps = reduce(values, (items, value, i) => {
      if (isArray(value)) {
        const subValues = this.renderArray(value);
        each(subValues, (vc, id) => {
          items.push(vc);
          if (id < subValues.length - 1) {
            items.push(<span key={`${i}-${id}-comma`}>, </span>);
          }
        });
      } else if (type === 'id' || indexOf(['opportunity_id', 'account_id', 'user_id'], name) >= 0) {
        items.push(this.renderIdValue(value, `${value}-${i}`));
      } else if (type === 'int') {
        items.push(<NumberDisplay key={`${value}-${i}`} value={value} precision={0} />);
      } else if (type === 'number' || type === 'double' || type === 'percent' || type === 'int') {
        items.push(this.renderNumberValue(value, type === 'int' ? 0 : 2, `${value}-${i}`));
      } else if (type === 'currency') {
        items.push(this.renderCurrencyValue(value, `${value}-${i}`));
      } else if (type === 'date') {
        items.push(<DateTimeDisplay key={`${value}-${i}`} value={value} format="short" />);
      } else if (type === 'boolean') {
        items.push(this.renderBooleanValue(value, `${value}-${i}`));
      } else if (type === 'picklist') {
        items.push(this.renderPicklistValue(value, `${value}-${i}`));
      } else {
        items.push(<span key={`${value}-${i}`}>{value}</span>);
      }
      if (i < values.length - 1) {
        items.push(<span key={`${i}-comma`}>; </span>);
      }
      return items;
    }, []);

    return comps;
  }

  renderValue() {
    const {
      value, valueFormatted, column, isCustomMeta,
    } = this.props;

    const { type } = column; // int, bool
    if (type === 'boolean') {
      return this.renderBooleanValue(value);
    }
    if (type === 'number' || type === 'double' || type === 'percent' || type === 'int') {
      return this.renderNumberValue(value, type === 'int' ? 0 : 2);
    }
    if (type === 'currency') {
      return this.renderCurrencyValue(value);
    }
    if (type === 'picklist') {
      return this.renderPicklistValue(isCustomMeta ? valueFormatted : value);
    }
    if (type === 'date') {
      return this.renderDateValue();
    }
    if (type === 'id' || indexOf(['opportunity_id', 'account_id', 'user_id'], column.target_field) >= 0) {
      return this.renderIdValue(value);
    }
    return (<span>{value}</span>);
  }

  renderGroupValue() {
    const {
      value, valueFormatted, column, isCustomMeta,
    } = this.props;
    const { type } = column;
    let comp = <span>{value}</span>;
    if (value !== null) {
      if (indexOf(['int', 'double', 'number', 'percent'], type) >= 0) {
        comp = this.renderNumberValue(value, type === 'int' ? 0 : 2);
      } else if (indexOf(['currency'], type) >= 0) {
        comp = this.renderCurrencyValue(value);
      } else if (indexOf(['boolean'], type) >= 0) {
        comp = this.renderBooleanValue(value);
      } else if (indexOf(['id'], type) >= 0) {
        comp = this.renderIdValue(value);
      } else if (indexOf(['picklist'], type) >= 0) {
        comp = this.renderPicklistValue(isCustomMeta ? valueFormatted : value);
      }
    }
    return comp;
  }

  render() {
    const { value, hidden } = this.props;
    if (hidden) {
      return null; // usefull for just to load data
    }
    if (isArray(value)) {
      return this.renderArray(uniq(flatten(value)));
    }
    if (this.props.node.group) {
      // groupping
      return this.renderGroupValue();
    }
    return this.renderValue();
  }
}

CustomMetaFieldRenderer.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    PropTypes.instanceOf(moment),
  ]),
  column: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string, // meta type, int, double, date, bool, etc...
  }).isRequired,
  customFieldType: PropTypes.string.isRequired,
  node: PropTypes.shape({
    group: PropTypes.bool,
  }),
  valueFormatted: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  isCustomMeta: PropTypes.bool,
  refData: PropTypes.shape({}),
  hidden: PropTypes.bool,
};
CustomMetaFieldRenderer.defaultProps = {
  value: null,
  node: {},
  valueFormatted: null,
  refData: {},
  isCustomMeta: false,
  hidden: false,
};

export default CustomMetaFieldRenderer;
